/**
 * The opening-hours entry form (admin Hours tab) ↔ HoursEntry.
 * Pure; the engine that reads these entries is utils/openingHours.ts.
 */
import type { ClockSeason, DayOfWeek, HoursDayType, HoursEntry } from '../types';
import { draftToTimeRule, TimeRuleDraft, timeRuleToDraft } from './timeRuleDraft';

/**
 * What a stored entry is, for sorting and labels: weekdays only, special days
 * (possibly with weekdays too), or one date.
 */
export type HoursEntryKind = 'regular' | 'special' | 'date';

/** What the form offers: "these days" (weekdays and/or special days) or one date. */
export type HoursDraftKind = 'days' | 'date';

/** When the entry applies during the year. */
export type HoursSeasonMode = 'allYear' | ClockSeason | 'dates';

export const HOURS_SEASON_MODES: HoursSeasonMode[] = ['allYear', 'winterClock', 'summerClock', 'dates'];

/** Special days an owner can give their own hours (plan 5.2). Order = display order. */
export const HOURS_SPECIAL_DAY_TYPES: HoursDayType[] = [
  'erevYomTov',
  'erevYomKippur',
  'erevTishaBav',
  'cholHaMoed',
  'yomTov',
  'roshHashana',
  'sukkotI',
  'simchatTorah',
  'pesachI',
  'shviiShelPesach',
  'shavuot',
  'yomKippur',
  'fastMinor',
  'tishaBav',
];

export const DAYS_OF_WEEK: DayOfWeek[] = [0, 1, 2, 3, 4, 5, 6];

export interface HoursEntryDraft {
  kind: HoursDraftKind;
  daysOfWeek: DayOfWeek[];
  dayTypes: HoursDayType[];
  specificDate: string;
  closed: boolean;
  open: TimeRuleDraft;
  close: TimeRuleDraft;
  noteHe: string;
  noteEn: string;
  season: HoursSeasonMode;
  validFrom: string;
  validTo: string;
}

export type HoursEntryError = 'daysRequired' | 'dateRequired' | 'seasonInvalid';

export function entryKind(entry: Pick<HoursEntry, 'specificDate' | 'dayTypes'>): HoursEntryKind {
  if (entry.specificDate) return 'date';
  if ((entry.dayTypes ?? []).some((d) => d !== 'weekday')) return 'special';
  return 'regular';
}

/** A new entry: Sunday–Thursday, 10:00–13:00. */
export function emptyHoursDraft(): HoursEntryDraft {
  return {
    kind: 'days',
    daysOfWeek: [0, 1, 2, 3, 4],
    dayTypes: [],
    specificDate: '',
    closed: false,
    open: timeRuleToDraft(undefined, '10:00'),
    close: timeRuleToDraft(undefined, '13:00'),
    noteHe: '',
    noteEn: '',
    season: 'allYear',
    validFrom: '',
    validTo: '',
  };
}

export function hoursEntryToDraft(entry: HoursEntry): HoursEntryDraft {
  return {
    kind: entry.specificDate ? 'date' : 'days',
    daysOfWeek: [...(entry.daysOfWeek ?? [])],
    dayTypes: (entry.dayTypes ?? []).filter((d) => d !== 'weekday'),
    specificDate: entry.specificDate ?? '',
    closed: entry.closed === true,
    open: timeRuleToDraft(entry.open, '10:00'),
    close: timeRuleToDraft(entry.close, '13:00'),
    noteHe: entry.noteHe ?? '',
    noteEn: entry.noteEn ?? '',
    season: entry.season ?? (entry.validFrom || entry.validTo ? 'dates' : 'allYear'),
    validFrom: entry.validFrom ?? '',
    validTo: entry.validTo ?? '',
  };
}

export function checkHoursDraft(draft: HoursEntryDraft): HoursEntryError[] {
  const errors: HoursEntryError[] = [];
  if (draft.kind === 'days' && draft.daysOfWeek.length === 0 && draft.dayTypes.length === 0) errors.push('daysRequired');
  if (draft.kind === 'date' && !draft.specificDate) errors.push('dateRequired');
  if (draft.season === 'dates' && draft.validFrom && draft.validTo && draft.validFrom > draft.validTo) errors.push('seasonInvalid');
  return errors;
}

/** The entry to store. Only the fields for the chosen kind are kept. */
export function draftToHoursEntry(draft: HoursEntryDraft, id: string): HoursEntry {
  const entry: HoursEntry = {
    id,
    daysOfWeek: draft.kind === 'days' ? [...draft.daysOfWeek].sort((a, b) => a - b) : [],
    dayTypes: draft.kind === 'days' ? [...draft.dayTypes] : [],
    open: draftToTimeRule(draft.open),
    close: draftToTimeRule(draft.close),
  };
  if (draft.closed) entry.closed = true;
  if (draft.kind === 'date') entry.specificDate = draft.specificDate;
  if (draft.noteHe.trim()) entry.noteHe = draft.noteHe.trim();
  if (draft.noteEn.trim()) entry.noteEn = draft.noteEn.trim();
  if (draft.kind !== 'date') {
    if (draft.season === 'winterClock' || draft.season === 'summerClock') entry.season = draft.season;
    if (draft.season === 'dates') {
      if (draft.validFrom) entry.validFrom = draft.validFrom;
      if (draft.validTo) entry.validTo = draft.validTo;
    }
  }
  return entry;
}

/**
 * Runs of consecutive days, for labels like "Sun–Thu, Sat".
 * [0,1,2,3,4,6] → [[0,4],[6,6]].
 */
export function dayRuns(days: DayOfWeek[]): [DayOfWeek, DayOfWeek][] {
  const sorted = Array.from(new Set(days)).sort((a, b) => a - b);
  const runs: [DayOfWeek, DayOfWeek][] = [];
  for (const d of sorted) {
    const last = runs[runs.length - 1];
    if (last && d === last[1] + 1) last[1] = d;
    else runs.push([d, d]);
  }
  return runs;
}

/** Entries in a sensible display order: regular by first day, then special days, then dates. */
export function sortHoursEntries<T extends Pick<HoursEntry, 'specificDate' | 'dayTypes' | 'daysOfWeek'>>(entries: T[]): T[] {
  const rank = { regular: 0, special: 1, date: 2 } as const;
  return [...entries].sort((a, b) => {
    const ka = entryKind(a);
    const kb = entryKind(b);
    if (ka !== kb) return rank[ka] - rank[kb];
    if (ka === 'date') return (a.specificDate ?? '').localeCompare(b.specificDate ?? '');
    if (ka === 'regular') return Math.min(...a.daysOfWeek, 7) - Math.min(...b.daysOfWeek, 7);
    return 0;
  });
}
